/**
 * GPS and Location Utilities
 * Helper functions for GPS tracking, distance calculation, and location validation
 */

/**
 * Convert degrees to radians
 * @param {number} deg - Degrees
 * @returns {number} Radians
 */
export const toRad = (deg) => {
  return deg * (Math.PI / 180);
};

/**
 * Calculate distance between two GPS coordinates using Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number} Distance in meters
 */
export const calcDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c * 1000; // Distance in meters
  return d;
};

/**
 * Check if geolocation permission is granted
 * @returns {Promise<boolean>} True if permission is granted
 */
export const checkLocationPermission = async () => {
  try {
    const { state } = await navigator.permissions.query({
      name: "geolocation",
    });
    return state !== "denied";
  } catch (error) {
    console.error("Error checking location permission:", error);
    return false;
  }
};

/**
 * Start watching user's GPS position
 * @param {Function} onSuccess - Callback for position updates
 * @param {Function} onError - Callback for errors
 * @returns {number} Watch ID for clearing
 */
export const startGPSTracking = (onSuccess, onError) => {
  return navigator.geolocation.watchPosition(
    (position) => {
      onSuccess({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
      });
    },
    (error) => {
      console.error("Geolocation error:", error);
      onError(error);
    },
    {
      enableHighAccuracy: true,
      maximumAge: 10000,
      timeout: 10000,
    }
  );
};

/**
 * Stop watching GPS position
 * @param {number} watchId - Watch ID returned from startGPSTracking
 */
export const stopGPSTracking = (watchId) => {
  if (watchId) {
    navigator.geolocation.clearWatch(watchId);
  }
};

/**
 * Open Google Maps with coordinates
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 */
export const openInGoogleMaps = (lat, lng) => {
  window.open(`https://maps.google.com?q=${parseFloat(lat)},${parseFloat(lng)}`);
};

/**
 * Format date for MTP comparison
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  const d = new Date(date);
  const options = {
    weekday: "short",
    month: "short",
    day: "2-digit",
    year: "numeric",
  };
  return d.toLocaleDateString("en-US", options);
};
